import { ONBOARDING_STEPS, type OnboardingStep } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyForm } from "../../features/company-form.js";
import { InviteForm } from "../../features/invite-form.js";
import { StationForm } from "../../features/station-form.js";
import { StationImport } from "../../features/station-import.js";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle, useOptimisticFlags } from "../../lib/hooks.js";
import { queryKeys } from "../../lib/query.js";
import { useMe } from "../../lib/session.js";

export const Route = createFileRoute("/_app/kurulum")({ component: OnboardingWizard });

/**
 * Kurulum sihirbazı (Prompt 04). Şirket/istasyon gibi veriler KENDİ uçlarıyla anında kaydedilir; sihirbaz yalnızca
 * ilerlemeyi saklar → yarıda bırakılıp başka cihazdan devam edilebilir. Tek istasyonlu işletmeci için sade:
 * her adımda tek bir form, zorunlu olmayan adımlar atlanabilir.
 */
function OnboardingWizard() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useDocumentTitle(t("onboarding.title"));

  const state = useQuery({
    queryKey: queryKeys.onboarding,
    queryFn: async () => (await api.GET("/tenant/onboarding")).data!,
  });
  const companies = useQuery({
    queryKey: queryKeys.companies,
    queryFn: async () => (await api.GET("/companies")).data!.items,
  });
  const stations = useQuery({
    queryKey: queryKeys.stations({ wizard: true }),
    queryFn: async () => (await api.GET("/stations", { params: { query: { limit: 200 } } })).data!.items,
  });
  const invitations = useQuery({
    queryKey: queryKeys.invitations,
    queryFn: async () => (await api.GET("/invitations", { params: { query: { limit: 50 } } })).data!.items,
  });

  const saveProgress = useMutation({
    mutationFn: async (next: { currentStep: OnboardingStep; completedSteps: OnboardingStep[] }) =>
      (await api.PUT("/tenant/onboarding", { body: next })).data!,
    onSuccess: (data) => queryClient.setQueryData(queryKeys.onboarding, data),
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  const complete = useMutation({
    mutationFn: async () => (await api.POST("/tenant/onboarding/complete")).data!,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      toast({ tone: "success", title: t("onboarding.completed") });
      await navigate({ to: "/" });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  if (!state.data) return <SkeletonRows rows={4} />;
  const current = state.data.currentStep;
  const currentIndex = ONBOARDING_STEPS.indexOf(current);
  const done = new Set(state.data.completedSteps);
  const go = (step: OnboardingStep, markDone?: OnboardingStep) =>
    saveProgress.mutate({
      currentStep: step,
      completedSteps: [...new Set([...done, ...(markDone ? [markDone] : [])])],
    });
  const next = () => go(ONBOARDING_STEPS[currentIndex + 1] ?? "summary", current);
  const back = () => go(ONBOARDING_STEPS[currentIndex - 1] ?? "companies");
  const blocked =
    (current === "companies" && (companies.data?.length ?? 0) === 0) ||
    (current === "stations" && (stations.data?.length ?? 0) === 0);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("onboarding.title")} description={t("onboarding.description")} />

      <ol className="mb-6 flex flex-wrap gap-2" aria-label={t("onboarding.steps")}>
        {ONBOARDING_STEPS.map((step, index) => {
          const isCurrent = step === current;
          const isDone = done.has(step) && !isCurrent;
          return (
            <li key={step}>
              <button
                type="button"
                onClick={() => go(step)}
                disabled={!isDone && !isCurrent}
                aria-current={isCurrent ? "step" : undefined}
                className={cn(
                  "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium disabled:opacity-60",
                  isCurrent && "border-primary bg-primary text-primary-foreground",
                  isDone && "border-success/40 bg-success-subtle text-success",
                )}
              >
                <span
                  className="flex size-5 items-center justify-center rounded-full bg-background/25 text-xs"
                  aria-hidden="true"
                >
                  {isDone ? <Check className="size-3.5" /> : index + 1}
                </span>
                {t(`onboarding.step.${step}.title`)}
                {isDone ? <span className="sr-only"> — {t("onboarding.stepDone")}</span> : null}
              </button>
            </li>
          );
        })}
      </ol>

      <Card className="p-5 sm:p-6">
        <h2 className="text-lg font-semibold">{t(`onboarding.step.${current}.title`)}</h2>
        <p className="mb-5 text-sm text-muted-foreground">{t(`onboarding.step.${current}.description`)}</p>

        {current === "companies" ? <CompaniesStep companies={companies.data ?? []} /> : null}
        {current === "stations" ? <StationsStep stations={stations.data ?? []} /> : null}
        {current === "businessUnits" ? <BusinessUnitsStep stations={stations.data ?? []} /> : null}
        {current === "invitations" ? (
          <InvitationsStep
            pending={(invitations.data ?? [])
              .filter((invitation) => invitation.status === "PENDING")
              .map((invitation) => invitation.email)}
          />
        ) : null}
        {current === "summary" ? (
          <dl className="grid gap-3 sm:grid-cols-3">
            {(
              [
                [t("onboarding.summary.companies"), companies.data?.length ?? 0],
                [t("onboarding.summary.stations"), stations.data?.length ?? 0],
                [t("onboarding.summary.invitations"), invitations.data?.length ?? 0],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="rounded-md border p-4">
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="text-2xl font-semibold tabular">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Card>

      <div className="mt-4 flex items-center justify-between gap-2">
        <Button variant="outline" onClick={back} disabled={currentIndex === 0 || saveProgress.isPending}>
          {t("common.back")}
        </Button>
        <div className="flex items-center gap-3">
          {blocked ? (
            <span className="text-sm text-muted-foreground">{t(`onboarding.step.${current}.required`)}</span>
          ) : null}
          {current === "summary" ? (
            <Button onClick={() => complete.mutate()} loading={complete.isPending}>
              {t("onboarding.finish")}
            </Button>
          ) : (
            <Button onClick={next} disabled={blocked} loading={saveProgress.isPending}>
              {current === "invitations" && (invitations.data?.length ?? 0) === 0
                ? t("common.skip")
                : t("common.continue")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SavedList({
  items,
  empty,
}: {
  items: Array<{ id: string; primary: string; secondary?: string | null }>;
  empty?: string;
}): ReactElement | null {
  if (items.length === 0) return empty ? <p className="mb-4 text-sm text-muted-foreground">{empty}</p> : null;
  return (
    <ul className="mb-5 grid gap-2">
      {items.map((item) => (
        <li key={item.id} className="flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <Check className="size-4 text-success" aria-hidden="true" />
          <span className="font-medium">{item.primary}</span>
          {item.secondary ? <span className="text-muted-foreground">{item.secondary}</span> : null}
        </li>
      ))}
    </ul>
  );
}

function CompaniesStep({
  companies,
}: {
  companies: Array<{ id: string; legalName: string; taxId: string }>;
}): ReactElement {
  const { t } = useTranslation();
  const canAddMore = (useMe().data?.activeTenant?.features ?? []).includes("MULTI_COMPANY");
  const [adding, setAdding] = useState(companies.length === 0);
  return (
    <>
      <SavedList
        items={companies.map((company) => ({
          id: company.id,
          primary: company.legalName,
          secondary: company.taxId,
        }))}
      />
      {adding || companies.length === 0 ? (
        <CompanyForm
          key={companies.length}
          onSaved={() => setAdding(false)}
          {...(companies.length > 0 ? { onCancel: () => setAdding(false) } : {})}
        />
      ) : canAddMore ? (
        <Button variant="outline" onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" />
          {t("onboarding.addAnotherCompany")}
        </Button>
      ) : null}
    </>
  );
}

function StationsStep({
  stations,
}: {
  stations: Array<{ id: string; code: string; name: string; city: string | null }>;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const [mode, setMode] = useState<"single" | "bulk" | null>(stations.length === 0 ? "single" : null);
  return (
    <>
      <SavedList
        items={stations.map((station) => ({
          id: station.id,
          primary: `${station.code} · ${station.name}`,
          secondary: station.city,
        }))}
      />
      {mode === "single" ? (
        <StationForm
          key={stations.length}
          onSaved={() => setMode(null)}
          {...(stations.length > 0 ? { onCancel: () => setMode(null) } : {})}
        />
      ) : null}
      {mode === "bulk" ? (
        <StationImport
          onImported={(created) => {
            toast({ tone: "success", title: t("stations.import.done", { count: created }) });
            setMode(null);
          }}
        />
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {mode !== "single" ? (
          <Button variant="outline" onClick={() => setMode("single")}>
            <Plus aria-hidden="true" />
            {stations.length === 0 ? t("stations.add") : t("onboarding.addAnotherStation")}
          </Button>
        ) : null}
        {mode !== "bulk" ? (
          <Button variant="ghost" onClick={() => setMode("bulk")}>
            {t("onboarding.bulkImport")}
          </Button>
        ) : null}
      </div>
    </>
  );
}

function BusinessUnitsStep({
  stations,
}: {
  stations: Array<{
    id: string;
    code: string;
    name: string;
    businessUnits: Array<{ unitType: string; isActive: boolean }>;
  }>;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const features = new Set(useMe().data?.activeTenant?.features ?? []);
  const units = ["FUEL", "LPG", "MARKET", "WASH", "EV", "CAFE", "RENTAL"] as const;
  const optimistic = useOptimisticFlags();
  const toggle = useMutation({
    mutationFn: async (input: { stationId: string; unitType: (typeof units)[number]; isActive: boolean }) =>
      api.PUT("/stations/{id}/business-units", {
        params: { path: { id: input.stationId } },
        body: { units: [{ unitType: input.unitType, isActive: input.isActive }] },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.stations() }),
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
    onSettled: (_data, _error, input) => optimistic.clear(`${input.stationId}:${input.unitType}`),
  });

  if (stations.length === 0) return <Alert tone="warning">{t("onboarding.step.stations.required")}</Alert>;
  return (
    <div className="grid gap-4">
      {stations.map((station) => (
        <fieldset key={station.id} className="rounded-md border p-4">
          <legend className="px-1 text-sm font-semibold">
            {station.code} · {station.name}
          </legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {units.map((unit) => {
              const active = optimistic.value(
                `${station.id}:${unit}`,
                station.businessUnits.some((item) => item.unitType === unit && item.isActive),
              );
              const enabled = features.has(unit);
              return (
                <label
                  key={unit}
                  className={cn("flex items-center gap-2 text-sm", !enabled && "text-muted-foreground")}
                  title={enabled ? undefined : t("stations.unitNotInPlan")}
                >
                  <Checkbox
                    checked={active}
                    disabled={!enabled || toggle.isPending}
                    onChange={(event) => {
                      optimistic.set(`${station.id}:${unit}`, event.target.checked);
                      toggle.mutate({
                        stationId: station.id,
                        unitType: unit,
                        isActive: event.target.checked,
                      });
                    }}
                  />
                  {t(`enums.businessUnit.${unit}`)}
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

function InvitationsStep({ pending }: { pending: string[] }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  return (
    <>
      {pending.length > 0 ? (
        <div className="mb-5 flex flex-wrap gap-2" aria-label={t("users.pendingInvitations")}>
          {pending.map((email) => (
            <StatusBadge key={email} tone="info">
              {email}
            </StatusBadge>
          ))}
        </div>
      ) : null}
      <InviteForm
        key={pending.length}
        onInvited={(email) => toast({ tone: "success", title: t("users.invitationSent", { email }) })}
      />
    </>
  );
}
